import { ClockTimestamp, now } from '@sqior/js/data';
import { DoNotCatchException } from '@sqior/js/error';
import { LogContent, LogData, LogLevel } from './log-data';
import { TextLogBackend } from './text-log-backend';

export type LogMessage = {
  level: LogLevel;
  content: LogContent;
  classified?: LogContent;
  timestamp?: ClockTimestamp;
};
export enum ErrorReportingMode {
  None = 'none',
  Log = 'log',
  Throw = 'throw',
  LogAndThrow = 'log-and-throw',
}
export type LogOptions = {
  level: LogLevel;
  logClassified?: boolean;
  validate?: boolean;
  errorReportingMode?: ErrorReportingMode;
};

export abstract class LogInterface {
  constructor(options: LogOptions) {
    this.options = options;
  }

  log(message: LogMessage) {
    if (message.level <= this.options.level)
      if (this.options.logClassified && message.classified)
        this.doLog({
          level: message.level,
          content: message.classified,
          timestamp: message.timestamp,
        });
      else
        this.doLog({
          level: message.level,
          content: message.content,
          timestamp: message.timestamp,
        });
  }

  offer(level: LogLevel, log: (logClassified: boolean) => LogContent) {
    if (level <= this.options.level)
      this.log({ level: level, content: log(this.options.logClassified || false) });
  }

  error(content: LogContent, classified?: LogContent) {
    this.log({
      level: LogLevel.Critical,
      content,
      classified,
      timestamp: now(),
    });
  }
  warn(content: LogContent, classified?: LogContent) {
    this.log({
      level: LogLevel.Warning,
      content,
      classified,
      timestamp: now(),
    });
  }
  info(content: LogContent, classified?: LogContent) {
    this.log({
      level: LogLevel.Info,
      content,
      classified,
      timestamp: now(),
    });
  }
  debug(content: LogContent, classified?: LogContent) {
    this.log({
      level: LogLevel.Debug,
      content,
      classified,
      timestamp: now(),
    });
  }
  trace(content: LogContent, classified?: LogContent) {
    this.log({
      level: LogLevel.Trace,
      content: content,
      classified: classified,
      timestamp: now(),
    });
  }

  /** Checks whether validation shall be done */
  get validate() {
    return this.options.validate ?? this.errorReportingMode !== ErrorReportingMode.None;
  }
  /** Sets the validation mode */
  set validate(mode: boolean) {
    this.options.validate = mode;
  }
  /** Returns the error report mode */
  get errorReportingMode() {
    return this.options.errorReportingMode ?? ErrorReportingMode.LogAndThrow;
  }
  /** Sets the error reporting mode */
  set errorReportingMode(mode: ErrorReportingMode) {
    this.options.errorReportingMode = mode;
  }
  /** Combined method that logs and throws depending on the mode specified */
  reportError(content: LogContent, mode?: ErrorReportingMode, classified?: LogContent) {
    mode = mode ?? this.errorReportingMode;
    if (mode === ErrorReportingMode.Log || mode === ErrorReportingMode.LogAndThrow)
      this.error(content, classified);
    if (mode === ErrorReportingMode.Throw || mode === ErrorReportingMode.LogAndThrow)
      throw new Error('ERROR: ' + TextLogBackend.convert(content));
  }

  exception(exc: unknown): string {
    /* Check if this is an exception type that is not supposed to be caught */
    if (exc instanceof DoNotCatchException) throw exc;
    if (exc instanceof Error) return exc.message;
    return JSON.stringify(exc);
  }
  timestamp(ts: ClockTimestamp) {
    return new Date(ts).toISOString();
  }

  protected abstract doLog(data: LogData): void;

  readonly options: LogOptions;
}

export function logLevel(level: string): LogLevel {
  switch (level.toLowerCase()) {
    case 'fatal':
      return LogLevel.Fatal;
    case 'critical':
      return LogLevel.Critical;
    case 'warning':
      return LogLevel.Warning;
    case 'info':
      return LogLevel.Info;
    case 'debug':
      return LogLevel.Debug;
    case 'trace':
      return LogLevel.Trace;
  }
  return LogLevel.Info;
}
